import type { CollectionName } from '@dovefiniscono/shared';
import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';
import type { SyncClient } from './client';
import type { SyncEngine, SyncStatus } from './engine';
import type { SyncStore } from './store';
import type { LocalRecord } from './types';

/** Live records of a collection; re-renders only when that collection changes. */
export function useRecords<C extends CollectionName>(
  store: SyncStore,
  collection: C,
): readonly LocalRecord<C>[] {
  return useSyncExternalStore(store.subscribe, () => store.list(collection));
}

export function useRecord<C extends CollectionName>(
  store: SyncStore,
  collection: C,
  id: string | null | undefined,
): LocalRecord<C> | undefined {
  return useSyncExternalStore(store.subscribe, () => (id ? store.get(collection, id) : undefined));
}

export function useSyncStatus(engine: SyncEngine): SyncStatus {
  return useSyncExternalStore(engine.subscribeStatus, () => engine.status);
}

const SyncContext = createContext<{ client: SyncClient; userId: string } | null>(null);

/** The signed-in user's local data, for every screen below. */
export function SyncProvider(props: { client: SyncClient; userId: string; children: ReactNode }) {
  const { client, userId, children } = props;
  const value = useMemo(() => ({ client, userId }), [client, userId]);
  return createElement(SyncContext.Provider, { value }, children);
}

function useSyncContext() {
  const value = useContext(SyncContext);
  if (!value) throw new Error('This hook must be used inside <SyncProvider>');
  return value;
}

export const useSyncClient = (): SyncClient => useSyncContext().client;
export const useSyncUserId = (): string => useSyncContext().userId;
export const useStore = () => useSyncClient().store;

export function useCollection<C extends CollectionName>(collection: C): readonly LocalRecord<C>[] {
  return useRecords(useStore(), collection);
}

export function useSyncState(): SyncStatus {
  return useSyncStatus(useSyncClient().engine);
}
